/**
 * GovRAMP + FedRAMP Class A on Today — V103-3-6 / V103-6-3 (design pass
 * `v103-programs-ui` round 4, `a-govramp.html?state=today*`).
 *
 *   GovrampTodayCard   the SAME status card as the program page (status line,
 *                      ConMon pips, controls subline, ONE date, ONE primary —
 *                      nearest hard date wins); the Snapshot world shows the
 *                      score bar with the last-submitted tick instead.
 *   FedrampGate        while Class A rests on GovRAMP Ready that isn't there
 *                      yet: a gate card ("Blocked · needs GovRAMP Ready", the
 *                      rules done, the target listing date, one action). While
 *                      GovRAMP is red (late / lapsed) it folds to one strip.
 *
 * FedrampGate falls back to the plain program card (loading, or the materials are
 * current). Items of the Programs list (li). Reads only (any member); the actions open
 * the program pages.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from '@aigovops/design-system';
import type { ProgramCard } from '../../lib/api';
import { daysBetween, daysText, fmtShort, govrampStatus, pct, snapshotLevers, type GovrampStatus } from '../../lib/govramp-model';
import { useClassA } from '../../lib/use-class-a';
import { GovrampStatusCard, useGovrampOverview } from '../programs/GovrampProgram';
import { Datebox, Glyph } from '../programs/govramp-parts';
import { getProgramManifest } from '@aigovops/core';
import { rollupHeadline, type RollupData } from '../sweep/ProgramRollup';
import '../../pages/govramp.css';

const primaryHref = (base: string, s: GovrampStatus) =>
  s.primary?.target === 'movers' ? `${base}/prepare#gr-movers-h` : `${base}#gr-conmon-h`;

/** `primary: false` when another part of Today holds the page's one filled primary (V108-6-5: the setup strip's fix). */
export function GovrampTodayCard({ card, prefix, slug, primary: mayFill = true, rollup = null, setupStep = null }: {
  card: ProgramCard; prefix: string; slug: string; primary?: boolean;
  /** BU-3: the program's roll-up once a sweep exists (status + count); ConMon stays the secondary line. */
  rollup?: RollupData | null;
  /** V108-8-16: GovRAMP's own next setup step (a quiet link, `ProgramSetupStep`). */
  setupStep?: ReactNode;
}) {
  const q = useGovrampOverview(slug, card.profile_id);
  const base = `${prefix}/programs/${card.program}`;
  if (q.isLoading) return <li className="gr-today" data-program="govramp"><Spinner label="Loading GovRAMP" /></li>;
  if (q.error || !q.data) return <li className="gr-today" data-program="govramp"><Alert variant="error"><p>GovRAMP could not load.</p><Link to={base}>Open GovRAMP</Link></Alert></li>;
  const o = q.data;
  const todayDay = o.generated_at.slice(0, 10);
  const s = govrampStatus(o, todayDay);
  if (s.world === 'snapshot') return <li className="gr-today" data-program="govramp" data-part="govramp-today"><SnapshotToday card={card} prefix={prefix} o={o} s={s} mayFill={mayFill} setupStep={setupStep} /></li>;
  const title = <Link to={base}>GovRAMP</Link>;
  const m = getProgramManifest(card.program);
  const head = rollup && m ? rollupHeadline(m, rollup) : null;
  return (
    <li className="gr-today" data-program="govramp" data-part="govramp-today">
      <GovrampStatusCard o={o} s={s} variant="today" title={title} rollup={head}
        primary={s.primary ? <Link className={`gp-linkbtn${mayFill ? ' btn-primary' : ''}`} to={primaryHref(base, s)} data-part="govramp-today-action">{s.primary.label}</Link> : <Link className="gp-linkbtn" to={base}>Open GovRAMP</Link>}>
        {setupStep}
      </GovrampStatusCard>
    </li>
  );
}

function SnapshotToday({ card, prefix, o, s, mayFill, setupStep }: { card: ProgramCard; prefix: string; o: NonNullable<ReturnType<typeof useGovrampOverview>['data']>; s: GovrampStatus; mayFill: boolean; setupStep?: ReactNode }) {
  const base = `${prefix}/programs/${card.program}`;
  const todayDay = o.generated_at.slice(0, 10);
  const snap = o.snapshot;
  const score = snap?.score.percent ?? null;
  const last = snap?.improvement.last_submitted_percent ?? null;
  const flat = !!snap?.improvement.warning;
  const lev = snapshotLevers(o);
  const projected = score != null ? Math.min(100, score + lev.fixGain + lev.collectGain) : null;
  const lastOn = o.last_submitted?.kind === 'snapshot' ? fmtShort(o.last_submitted.submitted_at, todayDay) : null;
  const delta = score != null && last != null ? Math.round((score - last) * 100) / 100 : null;
  return (
    <section className="gr-status" data-tone={s.tone} aria-labelledby="gr-today-snap-h">
      <h2 id="gr-today-snap-h" className="gr-prog-name"><Link to={base}>GovRAMP · Snapshot</Link></h2>
      <div className="gr-status-grid">
        <div className="gr-stack">
          <p className="gr-status-line"><Glyph status={flat ? 'act' : s.tone === 'bad' ? 'not' : 'done'} />{s.todayHeadline}</p>
          {score != null ? (
            <p className="gr-pipline">
              <span className="gr-scorebar gr-mini" data-tone={flat ? 'warn' : 'ok'} role="img"
                aria-label={`Score ${pct(score)}${last != null ? `; last submitted ${pct(last)}` : ''}${flat && projected! > score ? `; ${pct(projected!)} projected` : ''}`}>
                <span className="gr-fill" style={{ width: `${score}%` }} />
                {flat && projected! > score ? <span className="gr-proj" style={{ left: `${score}%`, width: `${projected! - score}%` }} /> : null}
                {last != null ? <span className="gr-tick" style={{ left: `${last}%` }} /> : null}
              </span>
              <span className="gr-num">{pct(score)}</span>
              {delta != null ? <span className="gr-trend"><span className={delta > 0 ? 'gr-up' : delta < 0 ? 'gr-down' : 'gr-flat'}><span aria-hidden="true">{delta > 0 ? '▲' : delta < 0 ? '▼' : '→'}</span>
                <span className="gp-sr-only">{delta > 0 ? 'up' : delta < 0 ? 'down' : 'unchanged'}</span>{` ${delta > 0 ? '+' : ''}${pct(delta)}`}</span></span> : null}
            </p>
          ) : <p className="gr-subline">{o.snapshot_error ?? 'Score not available yet'}</p>}
          {snap ? <p className="gr-subline">{`${snap.score.met} of ${snap.score.total} controls${last != null ? ` · last submitted ${pct(last)}${lastOn ? ` on ${lastOn}` : ''}` : ''}`}</p> : null}
          {setupStep}
        </div>
        {s.date ? <Datebox label={s.date.label} date={fmtShort(s.date.date, todayDay)} note={s.date.note} late={s.date.late} /> : null}
      </div>
      <div className="gr-actions">
        {flat ? <>
          {lev.fix.length ? <Link className={`gp-linkbtn${mayFill ? ' btn-primary' : ''}`} to={`${base}/prepare#gr-movers-h`}>{`Fix ${lev.fix.length} · +${pct(lev.fixGain)}`}</Link> : null}
          {lev.collect.length ? <Link className={`gp-linkbtn${lev.fix.length || !mayFill ? '' : ' btn-primary'}`} to={`${base}/prepare#gr-movers-h`}>{`Collect ${lev.collect.length} · +${pct(lev.collectGain)}`}</Link> : null}
        </> : <Link className={`gp-linkbtn${mayFill ? ' btn-primary' : ''}`} to={`${base}#gr-conmon-h`}>{s.primary?.label ?? 'Open GovRAMP'}</Link>}
      </div>
    </section>
  );
}

/**
 * FedRAMP 20x Class A on Today while it waits on GovRAMP Ready. Renders
 * nothing once the Ready materials are current (the plain card takes over).
 */
export function FedrampGate({ fr, gov, prefix, slug, fallback, setupStep = null }: { fr: ProgramCard; gov: ProgramCard | null; prefix: string; slug: string; fallback: ReactNode; setupStep?: ReactNode }) {
  const st = useClassA(slug, fr, gov);
  const frBase = `${prefix}/programs/${fr.program}`;
  const grBase = gov ? `${prefix}/programs/${gov.program}` : null;
  // Loading, not Class A, or the Ready materials are current: the plain program card.
  if (st.loading || !st.world || st.world.kind === 'materials-current') return <>{fallback}</>;
  const w = st.world;
  const todayDay = (st.gov?.generated_at ?? new Date().toISOString()).slice(0, 10);
  const s = st.gov ? govrampStatus(st.gov, todayDay) : null;
  const red = !!s && (s.lapsed || s.late);
  const planLabel = 'Plan Ready assessment';
  if (red && grBase) {
    return (
      <li className="gr-today" data-program="fedramp-cr26" data-part="fedramp-strip">
        <div className="gr-strip">
          <span><Glyph status="ns" /><Link className="gr-strong" to={frBase}>FedRAMP 20x</Link><span>Blocked · needs GovRAMP Ready</span></span>
          <span className="gr-small gr-dim">{`${planLabel} · ${s!.lapsed ? `After ${s!.held ? s!.held.charAt(0).toUpperCase() + s!.held.slice(1) : 'GovRAMP'} is restored` : `After ${s!.cycle?.label ?? 'ConMon'} is submitted`}`}</span>
        </div>
      </li>
    );
  }
  const c = st.counts;
  const target = st.listingDate ? null : st.targetDate;
  const action = w.kind === 'pursuing-ready' && !w.booking && grBase
    ? <Link className="gp-linkbtn" to={`${grBase}/prepare?book=1#gr-rd-h`}>Record Ready booking</Link>
    : w.kind === 'requires-ready' && grBase
      ? <Link className="gp-linkbtn" to={`${grBase}?tier=ready`}>{planLabel}</Link>
      : <Link className="gp-linkbtn" to={frBase}>Open Class A</Link>;
  return (
    <li className="gr-today" data-program="fedramp-cr26" data-part="fedramp-gate">
      <section className="gr-status" data-tone="gate" aria-labelledby="gr-today-fr-h">
        <h2 id="gr-today-fr-h" className="gr-prog-name"><Link to={frBase}>FedRAMP 20x · Class A</Link></h2>
        <div className="gr-status-grid">
          <div className="gr-stack">
            <p className="gr-status-line"><Glyph status="ns" />{w.kind === 'pursuing-ready' || w.kind === 'requires-ready' || w.kind === 'requires-ready-late' ? 'Blocked · needs GovRAMP Ready' : w.alarm}</p>
            {w.kind === 'pursuing-ready' ? <p className="gr-strong">{w.booking ? `Ready assessment booked · ${w.booking.firm} · ${fmtShort(w.booking.date, todayDay)}` : 'Ready assessment not booked'}</p> : null}
            {c && c.total ? (
              <p className="gr-subline"><span className="gr-thin" aria-hidden="true"><span style={{ width: `${Math.round((c.done / c.total) * 100)}%` }} /></span><span>{`${c.done} of ${c.total} rules done`}</span></p>
            ) : null}
            {setupStep}
          </div>
          {target ? <Datebox label="Target listing · at risk" date={fmtShort(target, todayDay)} note={daysText(daysBetween(todayDay, target))} /> : null}
        </div>
        <div className="gr-actions">{action}</div>
      </section>
    </li>
  );
}
